import React, { useCallback, useEffect, useState } from "react";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { unwrapPage } from "@/lib/paging";
import { ListPager } from "@/components/ui/list-pager";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  Inbox,
  Link2,
  Loader2,
  Mail,
  PlugZap,
  Trash2,
  Unplug,
  Users,
  XCircle,
} from "lucide-react";
import type {
  MailboxDefaults,
  MailboxInfo,
  MailboxTestResult,
  UnlinkedEmailRecord,
} from "@/types/email";
import { useConfirm } from "@/context/ConfirmContext";

const HOSTINGER_DEFAULTS: MailboxDefaults = {
  smtp_host: "smtp.hostinger.com",
  smtp_port: 465,
  imap_host: "imap.hostinger.com",
  imap_port: 993,
};

export const formatDateTime = (value?: string | null) =>
  value
    ? new Date(value).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "Never";

export function StatusBadge({ mailbox }: { mailbox: MailboxInfo | null }) {
  if (!mailbox) return <Badge variant="outline">Not connected</Badge>;
  if (mailbox.sync_status === "CONNECTED") return <Badge variant="success">Connected</Badge>;
  if (mailbox.sync_status === "AUTH_FAILED") return <Badge variant="destructive">Login failed</Badge>;
  return <Badge variant="warning">Sync problem</Badge>;
}

interface MailboxSettingsProps {
  /** Called whenever the connection changes, so the page header can update */
  onChange?: (mailbox: MailboxInfo | null) => void;
}

export function MailboxSettings({ onChange }: MailboxSettingsProps) {
  const { confirm } = useConfirm();
  const { user, hasPermission } = useAuth();
  const { success, error } = useToast();
  const canSend = hasPermission("email.send");

  const [mailbox, setMailbox] = useState<MailboxInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    email_address: "",
    password: "",
    display_name: "",
    ...HOSTINGER_DEFAULTS,
    sync_enabled: true,
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState<MailboxTestResult | null>(null);

  const loadMailbox = useCallback(async () => {
    try {
      const res = unwrapObject<{ mailbox: MailboxInfo | null; defaults: MailboxDefaults }>(await api.get("/api/Mailbox/Me"));
      const mb = res?.mailbox || null;
      setMailbox(mb);
      onChange?.(mb);
      setForm((f) => ({
        ...f,
        email_address: mb?.email_address || f.email_address || user?.Email || "",
        display_name: mb?.display_name || f.display_name || `${user?.FirstName || ""} ${user?.LastName || ""}`.trim(),
        smtp_host: mb?.smtp_host || res?.defaults?.smtp_host || HOSTINGER_DEFAULTS.smtp_host,
        smtp_port: mb?.smtp_port || res?.defaults?.smtp_port || HOSTINGER_DEFAULTS.smtp_port,
        imap_host: mb?.imap_host || res?.defaults?.imap_host || HOSTINGER_DEFAULTS.imap_host,
        imap_port: mb?.imap_port || res?.defaults?.imap_port || HOSTINGER_DEFAULTS.imap_port,
        sync_enabled: mb ? mb.sync_enabled : true,
        password: "",
      }));
    } catch (err: any) {
      error("Could not load mailbox", err.message);
    } finally {
      setLoading(false);
    }
  }, [user?.Email, user?.FirstName, user?.LastName, error, onChange]);

  useEffect(() => {
    loadMailbox();
  }, [loadMailbox]);

  const payload = () => ({
    email_address: form.email_address.trim(),
    password: form.password || undefined,
    display_name: form.display_name.trim() || undefined,
    smtp_host: form.smtp_host.trim(),
    smtp_port: Number(form.smtp_port),
    imap_host: form.imap_host.trim(),
    imap_port: Number(form.imap_port),
    sync_enabled: form.sync_enabled,
  });

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = unwrapObject<MailboxTestResult>(await api.post("/api/Mailbox/Test", payload()));
      setTestResult(res);
    } catch (err: any) {
      error("Test failed", err.message);
    } finally {
      setTesting(false);
    }
  };

  const handleConnect = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/api/Mailbox/Connect", payload());
      success("Mailbox connected", `Deal emails will now go out from ${form.email_address.trim()}`);
      setTestResult(null);
      await loadMailbox();
    } catch (err: any) {
      error("Could not connect", err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    if (!(await confirm({ title: "Disconnect your mailbox?", message: "Emails already on deals stay in the CRM.", confirmLabel: "Disconnect", destructive: true }))) return;
    try {
      await api.delete("/api/Mailbox/Disconnect");
      success("Mailbox disconnected");
      setMailbox(null);
      onChange?.(null);
      setTestResult(null);
    } catch (err: any) {
      error("Could not disconnect", err.message);
    }
  };

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <div className="space-y-4">
      {loading ? (
        <div className="py-12 flex justify-center">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {/* Connection status */}
          <div className="bg-card border rounded-2xl shadow-xs p-5 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <PlugZap className="size-5 text-primary" />
                <div>
                  <div className="text-sm font-semibold text-foreground">
                    {mailbox ? mailbox.email_address : "No mailbox connected"}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {mailbox
                      ? `Last checked for replies: ${formatDateTime(mailbox.last_synced_at)}${mailbox.sync_enabled ? "" : " (reply sync paused)"}`
                      : "Fill in the form below to connect."}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge mailbox={mailbox} />
                {mailbox && (
                  <Button variant="outline" size="sm" className="gap-1.5 text-destructive" onClick={handleDisconnect}>
                    <Unplug className="size-3.5" />
                    Disconnect
                  </Button>
                )}
              </div>
            </div>
            {mailbox && mailbox.sync_status !== "CONNECTED" && mailbox.last_error && (
              <div className="flex items-start gap-2 rounded-lg bg-destructive/10 text-destructive text-xs px-3 py-2">
                <AlertTriangle className="size-4 shrink-0 mt-px" />
                <span>
                  {mailbox.last_error}.{" "}
                  {mailbox.sync_status === "AUTH_FAILED" && "Enter your current password below and click Connect."}
                </span>
              </div>
            )}
          </div>

          {/* Connect / reconnect form */}
          {canSend && (
            <form onSubmit={handleConnect} className="bg-card border rounded-2xl shadow-xs p-5 space-y-4">
              <div>
                <h2 className="text-sm font-bold text-foreground">{mailbox ? "Update connection" : "Connect your mailbox"}</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Use the same email and password you use for Hostinger Webmail. The password is stored encrypted and
                  is never shown again.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Email address *</Label>
                  <Input type="email" required value={form.email_address} onChange={set("email_address")} placeholder="you@yourdomain.com" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Password {mailbox ? "(leave empty to keep current)" : "*"}</Label>
                  <Input
                    type="password"
                    autoComplete="new-password"
                    required={!mailbox}
                    value={form.password}
                    onChange={set("password")}
                    placeholder={mailbox ? "••••••••" : "Mailbox password"}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Name shown to customers</Label>
                  <Input value={form.display_name} onChange={set("display_name")} placeholder="Rahul Sharma - Nelgates" />
                </div>
                <div className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2">
                  <div>
                    <div className="text-xs font-medium">Sync replies into the CRM</div>
                    <div className="text-[11px] text-muted-foreground">Only emails with CRM contacts are imported</div>
                  </div>
                  <Switch checked={form.sync_enabled} onCheckedChange={(v) => setForm((f) => ({ ...f, sync_enabled: v }))} />
                </div>
              </div>

              <button
                type="button"
                onClick={() => setShowAdvanced((v) => !v)}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
              >
                <ChevronDown className={`size-3.5 transition-transform ${showAdvanced ? "" : "-rotate-90"}`} />
                Server settings (pre-filled for Hostinger)
              </button>
              {showAdvanced && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="space-y-1 col-span-2 sm:col-span-1">
                    <Label className="text-xs">SMTP host</Label>
                    <Input value={form.smtp_host} onChange={set("smtp_host")} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">SMTP port</Label>
                    <Input type="number" value={form.smtp_port} onChange={set("smtp_port")} />
                  </div>
                  <div className="space-y-1 col-span-2 sm:col-span-1">
                    <Label className="text-xs">IMAP host</Label>
                    <Input value={form.imap_host} onChange={set("imap_host")} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">IMAP port</Label>
                    <Input type="number" value={form.imap_port} onChange={set("imap_port")} />
                  </div>
                </div>
              )}

              {testResult && (
                <div className="rounded-lg border px-3 py-2 space-y-1 text-xs">
                  <TestLine ok={testResult.smtp_ok} label="Sending (SMTP)" error={testResult.smtp_error} />
                  <TestLine ok={testResult.imap_ok} label="Receiving replies (IMAP)" error={testResult.imap_error} />
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={handleTest} disabled={testing || saving}>
                  {testing && <Loader2 className="size-4 animate-spin mr-1.5" />}
                  Test connection
                </Button>
                <Button type="submit" disabled={saving || testing}>
                  {saving && <Loader2 className="size-4 animate-spin mr-1.5" />}
                  {mailbox ? "Save & reconnect" : "Connect"}
                </Button>
              </div>
            </form>
          )}

        </>
      )}
    </div>
  );
}

function TestLine({ ok, label, error }: { ok: boolean; label: string; error: string | null }) {
  return (
    <div className={`flex items-start gap-1.5 ${ok ? "text-emerald-600 dark:text-emerald-400" : "text-destructive"}`}>
      {ok ? <CheckCircle2 className="size-3.5 mt-px" /> : <XCircle className="size-3.5 mt-px" />}
      <span>
        {label}: {ok ? "OK" : error}
      </span>
    </div>
  );
}

/** Synced emails from known contacts that could not be matched to exactly one deal */
export function UnlinkedEmails({ onChanged }: { onChanged?: () => void }) {
  const { success, error } = useToast();
  const [emails, setEmails] = useState<UnlinkedEmailRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(30);
  const [choice, setChoice] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get(`/api/DealEmail/Unlinked?page=${page}`)
      .then((res) => {
        const paged = unwrapPage<UnlinkedEmailRecord>(res);
        setEmails(paged.rows);
        setTotal(paged.total);
        setPageSize(paged.page_size);
      })
      .catch(() => setEmails([]))
      .finally(() => setLoading(false));
  }, [page]);

  useEffect(load, [load]);

  const link = async (email: UnlinkedEmailRecord) => {
    const dealId = choice[email.id];
    if (!dealId) return;
    setBusyId(email.id);
    try {
      await api.post(`/api/DealEmail/Link/${email.id}`, { deal_id: dealId });
      success("Email linked", "The conversation now shows on the deal");
      load();
      onChanged?.();
    } catch (err: any) {
      error("Could not link", err.message);
    } finally {
      setBusyId(null);
    }
  };

  const dismiss = async (email: UnlinkedEmailRecord) => {
    setBusyId(email.id);
    try {
      await api.delete(`/api/DealEmail/Dismiss/${email.id}`);
      load();
      onChanged?.();
    } catch (err: any) {
      error("Could not remove", err.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="bg-card border rounded-2xl shadow-xs overflow-hidden">
      <div className="flex items-center gap-2 px-5 py-3 border-b">
        <Inbox className="size-4 text-primary" />
        <h2 className="text-sm font-bold text-foreground">Emails that need a deal</h2>
        {total > 0 && (
          <span className="text-[10px] px-1.5 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-400">{total}</span>
        )}
      </div>
      {loading ? (
        <div className="py-6 flex justify-center">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      ) : emails.length === 0 ? (
        <div className="py-5 px-5 text-xs text-muted-foreground">
          Nothing to sort. Emails from a contact who has more than one open deal (or none) appear here.
        </div>
      ) : (
        <ul className="divide-y">
          {emails.map((e) => (
            <li key={e.id} className="px-5 py-3 flex flex-col md:flex-row md:items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{e.subject || "(no subject)"}</div>
                <div className="text-xs text-muted-foreground truncate">
                  {e.direction === "IN" ? `From ${e.from_name || e.from_address}` : `To ${e.to_addresses.join(", ")}`}
                  {e.account_name && ` · ${e.account_name}`} · {formatDateTime(e.email_date)}
                </div>
                {e.snippet && <div className="text-xs text-muted-foreground/80 truncate mt-0.5">{e.snippet}</div>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {e.candidate_deals.length > 0 ? (
                  <>
                    <Select value={choice[e.id] || ""} onValueChange={(v) => setChoice((c) => ({ ...c, [e.id]: v }))}>
                      <SelectTrigger className="h-8 w-48 text-xs">
                        <SelectValue placeholder="Choose deal" />
                      </SelectTrigger>
                      <SelectContent>
                        {e.candidate_deals.map((d) => (
                          <SelectItem key={d.id} value={d.id} className="text-xs">
                            {d.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button size="sm" className="h-8 gap-1 text-xs" disabled={!choice[e.id] || busyId === e.id} onClick={() => link(e)}>
                      <Link2 className="size-3.5" />
                      Link
                    </Button>
                  </>
                ) : (
                  <span className="text-[11px] text-muted-foreground">No open deal for this account</span>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 w-8 p-0 text-muted-foreground"
                  title="Remove from CRM"
                  disabled={busyId === e.id}
                  onClick={() => dismiss(e)}
                >
                  <Trash2 className="size-3.5" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <ListPager page={page} pageSize={pageSize} total={total} onPage={setPage} disabled={loading} />
    </div>
  );
}

/** Admin overview of every rep's mailbox */
export function TeamMailboxes() {
  const [rows, setRows] = useState<MailboxInfo[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/api/Mailbox/All")
      .then((res) => setRows(unwrapArray<MailboxInfo>(res)))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="bg-card border rounded-2xl shadow-xs overflow-hidden">
      <div className="flex items-center gap-2 px-5 py-3 border-b">
        <Users className="size-4 text-primary" />
        <h2 className="text-sm font-bold text-foreground">Team mailboxes</h2>
        {rows.length > 0 && (
          <span className="ml-auto text-xs text-muted-foreground">
            {rows.filter((r) => r.sync_status === "CONNECTED").length} of {rows.length} connected
          </span>
        )}
      </div>
      {loading ? (
        <div className="py-6 flex justify-center">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      ) : rows.length === 0 ? (
        <div className="py-5 px-5 text-xs text-muted-foreground">No users in your team.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-muted-foreground">
              <tr className="border-b">
                <th className="text-left font-medium px-5 py-2">User</th>
                <th className="text-left font-medium px-3 py-2">Mailbox</th>
                <th className="text-left font-medium px-3 py-2">Status</th>
                <th className="text-left font-medium px-3 py-2">Last checked</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {rows.map((r) => (
                <tr key={r.user_id}>
                  <td className="px-5 py-2 font-medium">{r.user_name}</td>
                  <td className="px-3 py-2">{r.email_address || "—"}</td>
                  <td className="px-3 py-2">
                    <StatusBadge mailbox={r.sync_status === "NOT_CONNECTED" ? null : r} />
                    {r.sync_status !== "CONNECTED" && r.sync_status !== "NOT_CONNECTED" && r.last_error && (
                      <div className="text-[10px] text-muted-foreground mt-0.5">{r.last_error}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {r.sync_status === "NOT_CONNECTED" ? "—" : formatDateTime(r.last_synced_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

